import {
  ApplicationConfig,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeEsMX from '@angular/common/locales/es-MX';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { es_ES, provideNzI18n } from 'ng-zorro-antd/i18n';
import { ICONOS_PICKER_DEFINICIONES } from '@intelishs-group/ui';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { AuthService, AuthSesionService } from '@auth';
import {
  SESION_CONFIG,
  type SesionConfig,
  sesionExpiradaInterceptor,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { AccesosService } from '@services/accesos.service';
import { AccesosHttpService } from '@services/accesos-http.service';
import { environment } from 'src/environments/environment';

registerLocaleData(localeEsMX);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),

    // `credentialsInterceptor` va PRIMERO: pone `withCredentials` en cada
    // peticion, y sin el la cookie HttpOnly de sesion no se adjunta.
    // `sesionExpiradaInterceptor` convierte el 401 del backend en navegacion a
    // /login.
    provideHttpClient(withInterceptors([credentialsInterceptor, sesionExpiradaInterceptor])),

    provideNzI18n(es_ES),
    { provide: LOCALE_ID, useValue: 'es-MX' },

    // ng-zorro NO dibuja un icono que no este en el inyector, y uno elegido en
    // tiempo de EJECUCION —el de una aplicacion, un modulo o un rol— no se puede
    // resolver de otro modo. Son los 447 outline del paquete en 15 grupos, asi
    // que cubren tambien los `nzType` literales de las plantillas: no hace falta
    // una segunda lista para el chrome.
    provideNzIcons(ICONOS_PICKER_DEFINICIONES),

    // En produccion NO se cae al proveedor simulado: se lanza. Un build
    // productivo que aceptara la credencial de desarrollo es un incidente, no un
    // El ÚNICO proveedor de autenticación. Hubo uno simulado —`demo/demo`— que
    // existió para poder trabajar sin catálogo de usuarios, y se retiró en
    // cuanto el real funcionó.
    //
    // No por limpieza: un simulado que rellena huecos por otra vía NO los
    // cubre, los ESCONDE. Éste tapó tres, y los tres aparecieron el día que se
    // apagó — `nombre` y `correo` que el token no emitía, esos mismos que no se
    // conservaban al refrescar, y el rol del menú, que filtraba por
    // `administrador` porque era el literal que el simulado emitía. Con el real
    // el token trae `portal:superadmin` y el riel salía VACÍO: sin error, sin
    // ruta rota, sin nada en consola.
    // La sesion la emite IDENTIDAD y este portal solo la lee. Antes emitia la
    // suya —`AuthHttpService`— y el superadministrador acababa con dos sesiones
    // distintas segun por donde entrara.
    { provide: AuthService, useClass: AuthSesionService },

    // Se provee la CONFIGURACION, no `proveerSesion()`: ese registra su propio
    // inicializador y aqui ya hay uno. Dos leerian la sesion dos veces.
    //
    // Y va por `useFactory` porque `avisarSesionCaducada` necesita el
    // `NotifyService`, que es inyectable: un `useValue` no puede pedirlo.
    {
      provide: SESION_CONFIG,
      useFactory: (): SesionConfig => {
        const notify = inject(NotifyService);
        return {
          identidadUrl: environment.identidadUrl,
          loginUrl: environment.loginUrl,

          // El aviso antes de mandar al login. Es obligatorio en la libreria, y
          // no por gusto: la primera version redirigia en silencio y se comio un
          // aviso que llevaba años en produccion.
          avisarSesionCaducada: () => notify.unauthorizedNotify(),
        };
      },
    },

    // El gestor de accesos habla con `AdminPortal.API` sobre el esquema `acceso`
    // de la base propia. `AccesosService` es abstracta —igual que
    // `AuthService`— asi que esta es la unica linea que distingue una
    // implementacion de otra.
    //
    // Hubo una implementacion `AccesosMockService` sobre `localStorage`,
    // mientras el esquema de la base se reestructuraba. Ya se BORRO: su unico
    // valor restante era la curaduria de iconos, nombres y descripciones de las
    // aplicaciones que faltaban, y esa quedo transcrita en la seccion 7 de
    // `alcance-aplicaciones-no-migradas.txt`.
    //
    // No se dejo tras un flag, y eso fue deliberado: dos implementaciones del
    // mismo contrato con las mismas reglas de negocio divergen a la primera
    // correccion, y un flag que nadie mueve es duplicacion permanente.
    { provide: AccesosService, useClass: AccesosHttpService },

    // Rehidrata la sesion ANTES del primer render: sin esto, un F5 dentro de la
    // app pinta el login por un instante y el parpadeo parece un cierre de
    // sesion espurio.
    provideAppInitializer(() => firstValueFrom(inject(AuthService).restaurarSesion())),
  ],
};
